"use client";
import React, { useState, useEffect } from "react";
import { Mail, ArrowLeft, CheckCircle, Lock, Eye, EyeOff } from "lucide-react";
import { forgotPassword, resetPassword } from "../../services/auth";
import { Link, useSearchParams, useNavigate } from "react-router-dom";

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get token and email from query parameters
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");
  const isResetMode = !!token && !!emailParam;

  // Form states
  const [email, setEmail] = useState(emailParam || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => {
    // Update email if it comes from query params
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [emailParam]);

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setFieldErrors({ email: "Please enter your email address" });
      return;
    } else if (!emailRegex.test(email)) {
      setFieldErrors({ email: "Invalid Email format" });
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const newFieldErrors: any = {};
    if (!email) newFieldErrors.email = "Email is required";
    if (!password) newFieldErrors.password = "Password is required";
    if (password.length < 8)
      newFieldErrors.password = "Password must be at least 8 characters";
    if (password !== confirmPassword)
      newFieldErrors.confirmPassword = "Passwords do not match";

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    setLoading(true);
    try {
      await resetPassword({
        email,
        password,
        password_confirmation: confirmPassword,
        token: token || undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Reset failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="h-screen flex font-sans bg-white overflow-hidden">
        {/* Left Side - Success Message */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-12 lg:px-20 overflow-y-auto">
          <div className="max-w-[340px] w-full mx-auto py-8">
            {/* Logo */}
            <div className="mb-8">
              <img
                src="/glossa-purple.png"
                alt="Glossa"
                className="h-8 sm:h-9 max-w-[120px] pointer-events-none select-none"
              />
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-green-50 p-4 rounded-full">
                  <CheckCircle className="text-green-500 w-10 h-10" />
                </div>
              </div>

              {isResetMode ? (
                <>
                  <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                    Password Reset Successful
                  </h1>
                  <p className="text-gray-500 text-[13px] mb-8">
                    Your password has been reset successfully. You can now log in with your new password.
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center w-full bg-[#885ABB] hover:bg-[#7647a8] text-white h-[42px] rounded-lg font-bold text-sm transition-all shadow-lg shadow-purple-500/30"
                  >
                    Go to Login
                  </Link>
                </>
              ) : (
                <>
                  <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                    Check your email
                  </h1>
                  <p className="text-gray-500 text-[13px] mb-8 leading-relaxed">
                    We've sent a password reset link to <br />
                    <span className="font-semibold text-gray-900">{email}</span>
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-[#885ABB] font-bold text-sm hover:underline"
                  >
                    <ArrowLeft size={16} />
                    Back to Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Image */}
        <div className="hidden lg:block lg:w-1/2 relative bg-gray-50 max-h-screen overflow-hidden">
          <img
            src="/auth-cover.png"
            alt="Glossa"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex font-sans bg-white overflow-hidden">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-12 lg:px-20 overflow-y-auto">
        <div className="max-w-[340px] w-full mx-auto py-8">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/glossa-purple.png"
              alt="Glossa"
              className="h-8 sm:h-9 max-w-[120px] pointer-events-none select-none"
            />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
              {isResetMode ? "Reset password" : "Forgot password?"}
            </h1>
            <p className="text-gray-500 text-[13px]">
              {isResetMode
                ? "Please enter your new password below"
                : "No worries, we'll send you reset instructions"}
            </p>
          </div>

          {isResetMode ? (
            // Reset Password Form
            <form
              className="space-y-4"
              onSubmit={handleResetPasswordSubmit}
              noValidate
            >
              <div className="space-y-3">
                {/* Email Field (ReadOnly) */}
                <div className="space-y-1">
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="w-full h-[42px] px-4 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-500 font-normal outline-none cursor-not-allowed"
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-1">
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="New password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (fieldErrors.password)
                          setFieldErrors({ ...fieldErrors, password: "" });
                      }}
                      className={`w-full h-[42px] px-4 bg-[#F9FAFB] border ${fieldErrors.password ? "border-red-500" : "border-gray-100"
                        } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400 font-normal`}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB]"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p className="text-[11px] text-red-500 font-medium ml-1">
                      {fieldErrors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-1">
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (fieldErrors.confirmPassword)
                          setFieldErrors({
                            ...fieldErrors,
                            confirmPassword: "",
                          });
                      }}
                      className={`w-full h-[42px] px-4 bg-[#F9FAFB] border ${fieldErrors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-100"
                        } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400 font-normal`}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB]"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && (
                    <p className="text-[11px] text-red-500 font-medium ml-1">
                      {fieldErrors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <div className="p-3 text-red-600 bg-red-50 text-[11px] rounded-lg border border-red-100 flex items-center gap-2">
                  <p className="font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#885ABB] hover:bg-[#7647a8] text-white h-[42px] rounded-lg font-bold text-sm transition-all disabled:opacity-50 shadow-lg shadow-purple-500/30"
              >
                {loading ? "Updating..." : "Update password"}
              </button>
            </form>
          ) : (
            // Forgot Password Form
            <form
              className="space-y-4"
              onSubmit={handleForgotPasswordSubmit}
              noValidate
            >
              <div className="space-y-1">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) setFieldErrors({});
                  }}
                  className={`w-full h-[42px] px-4 bg-[#F9FAFB] border ${fieldErrors.email ? "border-red-500" : "border-gray-100"
                    } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400 font-normal`}
                />
                {fieldErrors.email && (
                  <p className="text-[11px] text-red-500 font-medium ml-1">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {error && (
                <div className="p-3 text-red-600 bg-red-50 text-[11px] rounded-lg border border-red-100 flex items-center gap-2">
                  <p className="font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#885ABB] hover:bg-[#7647a8] text-white h-[42px] rounded-lg font-bold text-sm transition-all disabled:opacity-50 shadow-lg shadow-purple-500/30"
              >
                {loading ? "Sending..." : "Reset password"}
              </button>
            </form>
          )}

          <div className="mt-8 text-center text-[11px] font-medium">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#885ABB] transition-colors"
            >
              <ArrowLeft size={14} />
              {isResetMode ? "Back to Login" : "Return to Login"}
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-gray-50 max-h-screen overflow-hidden">
        <img
          src="/auth-cover.png"
          alt="Glossa"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
      </div>
    </div>
  );
}
